import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth, ROLE_LABELS } from "@/lib/auth";
import mark from "@/assets/narad-mark.jpg";

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8" height="8" fill="#F25022" />
      <rect x="10" y="0" width="8" height="8" fill="#7FBA00" />
      <rect x="0" y="10" width="8" height="8" fill="#00A4EF" />
      <rect x="10" y="10" width="8" height="8" fill="#FFB900" />
    </svg>
  );
}

export default function Login() {
  const [accounts, setAccounts] = useState([]);
  const [busy, setBusy] = useState(null);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const { login, member } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (member) navigate("/"); }, [member, navigate]);
  useEffect(() => { api.get("/auth/accounts").then((res) => setAccounts(res.data)).catch(() => {}); }, []);

  const signIn = async (addr) => {
    setBusy(addr);
    try { await login(addr); navigate("/"); } finally { setBusy(null); }
  };

  return (
    <div className="min-h-screen w-full flex flex-col lg:flex-row">
      {/* Left — navy */}
      <div className="lg:w-[760px] px-8 lg:px-16 py-14 flex flex-col justify-between" style={{ background: "var(--brand-navy)" }}>
        <div className="flex items-center gap-4">
          <span className="rounded-xl flex items-center justify-center overflow-hidden" style={{ width: 56, height: 56, background: "var(--brand-silky)", padding: 6 }}>
            <img src={mark} alt="Narad" className="object-contain" style={{ mixBlendMode: "multiply" }} />
          </span>
          <span className="font-display font-extrabold" style={{ color: "var(--brand-silky)", fontSize: 32 }}>Narad</span>
        </div>

        <div className="pt-12" data-testid="login-headline">
          <h1 className="font-display font-extrabold leading-[1.05]" style={{ color: "var(--brand-silky)", fontSize: 56 }}>
            Know which AI your firm{" "}
            <span className="inline-block rounded-[10px] px-2" style={{ background: "var(--brand-lime)", color: "var(--brand-navy)" }}>really</span>{" "}
            uses.
          </h1>
          <p className="mt-6 max-w-[560px]" style={{ color: "rgba(247,243,230,0.8)", fontSize: 18, lineHeight: "28px" }}>
            Adoption, spend and renewals for every AI tool you pay for, in one calm view, so the next contract decision is made on evidence.
          </p>
        </div>
      </div>

      {/* Right — cream */}
      <div className="flex-1 flex items-center justify-center px-6 py-14" style={{ background: "var(--brand-silky)" }}>
        <div className="w-full max-w-[420px]">
          <h2 className="font-display font-extrabold text-nb-ink" style={{ fontSize: 40 }}>Welcome back</h2>
          <p className="text-[15px] text-nb-ink2 mt-2">Sign in with your firm account to see this month's briefing.</p>

          <button
            data-testid="continue-microsoft"
            onClick={() => accounts[0] && signIn(accounts.find((a) => a.role === "admin")?.email || accounts[0].email)}
            className="w-full h-11 mt-6 rounded-[10px] bg-nb-surface flex items-center justify-center gap-2.5 text-[14px] font-semibold text-nb-ink transition-colors hover:bg-[var(--bg-selected)]"
            style={{ border: "1px solid var(--border-control)" }}
          >
            <MicrosoftIcon /> Continue with Microsoft
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px" style={{ background: "var(--border-subtle)" }} />
            <span className="text-[12px] text-nb-ink2">or</span>
            <div className="flex-1 h-px" style={{ background: "var(--border-subtle)" }} />
          </div>

          <label className="nb-label block mb-1.5">Work email</label>
          <input
            data-testid="login-email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@firm.com"
            className="w-full h-11 px-3.5 rounded-[10px] bg-nb-surface text-[14px] text-nb-ink placeholder:text-nb-ink2 focus:outline-none"
            style={{ border: "1px solid var(--border-control)" }}
          />
          <button
            data-testid="send-link"
            onClick={() => setSent(true)}
            className="w-full h-11 mt-3 rounded-[10px] bg-nb-navy text-nb-cream text-[14px] font-semibold transition-opacity hover:opacity-90"
          >
            Send me a sign-in link
          </button>

          {sent && (
            <div className="mt-4 rounded-[10px] px-4 py-3 text-[13px] text-nb-ink" style={{ background: "var(--brand-floral)" }} data-testid="sent-banner">
              Check your inbox: a sign-in link is on its way.
            </div>
          )}

          {/* Demo accounts (mock SSO) — keeps the preview usable without email */}
          <div className="mt-8 pt-6" style={{ borderTop: "1px solid var(--border-subtle)" }}>
            <p className="nb-label mb-2">Demo accounts</p>
            <div className="space-y-2">
              {accounts.map((a) => (
                <button
                  key={a.email}
                  data-testid={`login-${a.role}`}
                  disabled={busy}
                  onClick={() => signIn(a.email)}
                  className="w-full text-left px-3.5 py-2.5 rounded-[10px] bg-nb-surface flex items-center justify-between group disabled:opacity-60 hover:bg-[var(--bg-selected)] transition-colors"
                  style={{ border: "1px solid var(--border-subtle)" }}
                >
                  <span className="text-[13px] font-semibold text-nb-ink">{ROLE_LABELS[a.role]}</span>
                  <ArrowRight size={15} strokeWidth={1.75} className="text-nb-ink2 group-hover:text-nb-ink transition-colors" />
                </button>
              ))}
            </div>
          </div>

          <p className="text-[12px] text-nb-ink2 mt-6">
            Access is limited to admins your firm has approved. <span className="font-medium" style={{ color: "var(--text-link)" }}>Request access</span>
          </p>
        </div>
      </div>
    </div>
  );
}
